/** @format */

import { CONTENT_LANGUAGES, SEARCH_SORT_OPTIONS } from 'src/constants';
import { getDefaultSearchLanguage } from 'src/slices/search';
import { isExtranetRemovedLocale } from 'src/utils/extranetLanguages';

const BRAND_VALUES = new Set(['all', 'ate', 'vdo']);
const SORT_VALUES = new Set(SEARCH_SORT_OPTIONS.map(option => option.value));
const LANGUAGE_VALUES = new Set(CONTENT_LANGUAGES.map(language => language.value));

export interface SearchUrlState {
  brand: string;
  sortBy: string;
  topicId: number | null;
  language?: string;
  keywords?: string[];
}

export const SEARCH_URL_DEFAULTS: Pick<SearchUrlState, 'brand' | 'sortBy' | 'topicId'> = {
  brand: 'all',
  sortBy: 'latest',
  topicId: null
};

function normalizeBrand(value?: string | null): string {
  const normalized = String(value || '')
    .trim()
    .toLowerCase();
  return BRAND_VALUES.has(normalized) ? normalized : SEARCH_URL_DEFAULTS.brand;
}

function normalizeSort(value?: string | null): string {
  const normalized = String(value || '')
    .trim()
    .toLowerCase();
  return SORT_VALUES.has(normalized) ? normalized : SEARCH_URL_DEFAULTS.sortBy;
}

function normalizeLanguage(value: string | null, defaultLanguage: string): string {
  const normalized = String(value || '')
    .trim()
    .toLowerCase();
  if (!normalized) {
    return defaultLanguage;
  }
  const exact = [...LANGUAGE_VALUES].find(language => language.toLowerCase() === normalized);
  if (!exact || isExtranetRemovedLocale(exact)) {
    return defaultLanguage;
  }
  return exact;
}

function normalizeTopicId(value: string | null): number | null {
  const normalized = String(value || '').trim();
  if (!normalized) {
    return null;
  }
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

export function parseSearchUrlSearch(searchString: string): SearchUrlState {
  const defaultLanguage = getDefaultSearchLanguage();
  const params = new URLSearchParams(searchString.startsWith('?') ? searchString.slice(1) : searchString);

  return {
    brand: normalizeBrand(params.get('brand')),
    topicId: normalizeTopicId(params.get('topic')),
    language: normalizeLanguage(params.get('language'), defaultLanguage),
    sortBy: normalizeSort(params.get('sort')),
    keywords: params
      .getAll('keywords[]')
      .map(keyword => keyword.trim())
      .filter(Boolean)
  };
}

function buildSearchUrlSearchParams({ brand, topicId, language, sortBy, keywords }: SearchUrlState): URLSearchParams {
  const defaultLanguage = getDefaultSearchLanguage();
  const params = new URLSearchParams();

  if (brand && brand !== SEARCH_URL_DEFAULTS.brand) {
    params.set('brand', brand);
  }
  if (topicId) {
    params.set('topic', String(topicId));
  }
  if (language && language !== defaultLanguage) {
    params.set('language', language);
  }
  if (sortBy && sortBy !== SEARCH_URL_DEFAULTS.sortBy) {
    params.set('sort', sortBy);
  }
  (Array.isArray(keywords) ? keywords : []).forEach(keyword => {
    params.append('keywords[]', keyword);
  });

  return params;
}

export function buildSearchUrlSearch(state: Partial<SearchUrlState> & { keywords?: string[] }): string {
  const query = buildSearchUrlSearchParams({
    brand: state.brand ?? SEARCH_URL_DEFAULTS.brand,
    sortBy: state.sortBy ?? SEARCH_URL_DEFAULTS.sortBy,
    topicId: state.topicId ?? SEARCH_URL_DEFAULTS.topicId,
    language: state.language,
    keywords: state.keywords
  }).toString();
  return query ? `?${query}` : '';
}
